import { useSession, type Session } from '../store/session'

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''
const APPVIEW = 'https://public.api.bsky.app'

function apiUrl(path: string): string {
  return API_BASE ? `${API_BASE}${path}` : path
}

interface LoginPayload {
  service: string
  identifier: string
  password: string
  authFactorToken?: string
}

interface LoginResponse {
  did: string
  handle: string
  accessJwt: string
  refreshJwt: string
}

export async function login(p: LoginPayload): Promise<LoginResponse> {
  const r = await fetch(apiUrl('/api/auth/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(p),
  })
  const j = (await r.json()) as LoginResponse & { error?: string; message?: string }
  if (!r.ok) {
    throw new Error(j.error ?? j.message ?? `login failed (${r.status})`)
  }
  return j
}

async function refresh(session: Session): Promise<Session> {
  const r = await fetch(apiUrl('/api/auth/refresh'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ service: session.service, refreshJwt: session.refreshJwt }),
  })
  if (!r.ok) throw new Error(`refresh failed (${r.status})`)
  const j = (await r.json()) as { accessJwt: string; refreshJwt: string }
  const next: Session = { ...session, accessJwt: j.accessJwt, refreshJwt: j.refreshJwt }
  useSession.getState().setSession(next)
  return next
}

/** AppView GET — unauthenticated, hits public.api.bsky.app directly.
 *  Used for reads (profiles, feeds, followers, likes). */
export async function appView<T>(path: string, params?: Record<string, string | number>): Promise<T> {
  const qs = params
    ? '?' +
      Object.entries(params)
        .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
        .join('&')
    : ''
  const r = await fetch(`${APPVIEW}/xrpc/${path}${qs}`)
  if (!r.ok) throw new Error(`appView ${path} failed: ${r.status}`)
  return (await r.json()) as T
}

/** Authenticated XRPC via the worker proxy. Auto-refreshes once on
 *  ExpiredToken. Used for reads tied to the logged-in account (e.g.
 *  /com.atproto.repo.listRecords for our own likes). */
export async function xrpc<T>(opts: {
  path: string
  body?: unknown
  method?: 'GET' | 'POST'
}): Promise<T> {
  let session = useSession.getState().session
  if (!session) throw new Error('not logged in')
  const doFetch = async (s: Session) =>
    fetch(apiUrl('/api/xrpc'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        service: s.service,
        accessJwt: s.accessJwt,
        path: opts.path,
        body: opts.body,
        method: opts.method ?? 'POST',
      }),
    })
  let r = await doFetch(session)
  if (r.status === 401 || r.status === 400) {
    const txt = await r.clone().text()
    if (/ExpiredToken/i.test(txt)) {
      session = await refresh(session)
      r = await doFetch(session)
    }
  }
  if (!r.ok) {
    const txt = await r.text()
    throw new Error(`xrpc ${opts.path}: ${r.status} ${txt}`)
  }
  return (await r.json()) as T
}

// ─────────── helpers used by Tiles + Spider ───────────

export interface Profile {
  did: string
  handle: string
  displayName?: string
  avatar?: string
  description?: string
  followersCount?: number
  followsCount?: number
}

export async function resolveHandle(handle: string): Promise<string> {
  const r = await appView<{ did: string }>('com.atproto.identity.resolveHandle', {
    handle,
  })
  return r.did
}

export async function getProfile(actor: string): Promise<Profile> {
  return appView<Profile>('app.bsky.actor.getProfile', { actor })
}

export interface FeedItem {
  post: {
    uri: string
    cid: string
    author: { did: string; handle: string; displayName?: string; avatar?: string }
    record: { text?: string; createdAt?: string }
    embed?: import('../components/PostMedia').PostEmbedView
    labels?: { val: string }[]
    indexedAt: string
    likeCount?: number
    repostCount?: number
    replyCount?: number
  }
}

export async function getAuthorFeed(actor: string, limit = 30): Promise<FeedItem[]> {
  const r = await appView<{ feed?: FeedItem[] }>('app.bsky.feed.getAuthorFeed', {
    actor,
    limit,
  })
  return r.feed ?? []
}

export async function getFollows(actor: string, limit = 100): Promise<Profile[]> {
  const r = await appView<{ follows?: Profile[] }>('app.bsky.graph.getFollows', {
    actor,
    limit,
  })
  return r.follows ?? []
}

/** People who liked a post. Capped at `limit` per call. */
export async function getLikers(
  postUri: string,
  limit = 25,
): Promise<{ actor: Profile }[]> {
  const r = await appView<{ likes?: { actor: Profile }[] }>('app.bsky.feed.getLikes', {
    uri: postUri,
    limit,
  })
  return r.likes ?? []
}

/** Posts the actor liked. `getActorLikes` is authenticated AppView only —
 *  Bluesky's public AppView returns 401, so we hit api.bsky.app directly
 *  with the session bearer (Bluesky allows CORS from any origin). Only
 *  the logged-in actor's own likes are returnable; calls with a different
 *  `actor` will 4xx — Spider locks the input to session.handle. */
export async function getActorLikes(actor: string, limit = 50): Promise<FeedItem[]> {
  const session = useSession.getState().session
  if (!session) throw new Error('not logged in')
  const url = `https://api.bsky.app/xrpc/app.bsky.feed.getActorLikes?actor=${encodeURIComponent(actor)}&limit=${limit}`
  let r = await fetch(url, { headers: { Authorization: `Bearer ${session.accessJwt}` } })
  if (r.status === 401 || r.status === 400) {
    const txt = await r.clone().text()
    if (/ExpiredToken/i.test(txt)) {
      const next = await refresh(session)
      r = await fetch(url, { headers: { Authorization: `Bearer ${next.accessJwt}` } })
    }
  }
  if (!r.ok) throw new Error(`getActorLikes ${r.status}`)
  const j = (await r.json()) as { feed?: FeedItem[] }
  return j.feed ?? []
}
